import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { MobXProviderContext } from "mobx-react";
import { useNavigate } from "react-router-dom";
import { Toast } from "primereact/toast";
import { ProgressSpinner } from "primereact/progressspinner";
import { ROLE_TYPE_MHP, ROLE_TYPE_NURSE, ROLE_TYPE_PROVIDER } from "../../../constants/RegisterConstants";
import { useResponsive } from "../../../responsive/ResponsiveProvider";
import Unauthorized from "../../../shared/pages/Unauthorized";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { confirmDialog } from "primereact/confirmdialog";
import { InputText } from "primereact/inputtext";
import { MenuItem } from "primereact/menuitem";
import { Tag } from "primereact/tag";
import { InputSwitch } from "primereact/inputswitch";
import ActionMenuButton from "../../../components/general/ActionMenuButton";
import ResidentEntryTrackerButton from "../../resident/components/ResidentEntryTrackerButton";
import SearchAddress from "../../../components/general/SearchAddress";
import { RouterAddress } from "../../../constants/RouterAddress";
import { SHOW_RESIDENT_SCHEDULES } from "../../../constants/uiFlags";
import { getErrorTitle, USER_GROUP_ADMIN } from "../../../constants/constants";
import { extractLocationDetails } from "../../../utils/stringUtils";
import { useTour } from "../../../shared/tour/TourProvider";
import {
  dashboardPageContainerStyle,
  dashboardSectionCardStyle,
  renderDashboardHeader,
} from "./dashboardStyles";
import {
  appAccentPanelStyle,
  appDialogContentStyle,
  appDialogHeaderStyle,
  appDialogStyle,
  appFormSectionStyle,
  appMutedTextStyle,
  renderSurfaceHeader,
} from "../../../components/general/uiSurfaceStyles";
import { useAuthExperience } from "../../auth/hooks/useAuthExperience";
type MhpDashboardWorkspaceProps = {
  workspaceTitle?: string;
  workspaceDescription?: string;
};

export const MhpDashboardWorkspace = ({
  workspaceTitle = "MHP Dashboard",
  workspaceDescription = "",
}: MhpDashboardWorkspaceProps) => {
  const normalizeSharedClinicalAccess = (
    residentLink: any,
    accessType: "assessment" | "carePlan" | "delegation",
  ) => {
    if (accessType === "delegation") {
      return residentLink?.delegationAccessEnabled === true;
    }
    if (accessType === "assessment") {
      return residentLink?.assessmentAccessEnabled !== false;
    }
    return residentLink?.carePlanAccessEnabled !== false;
  };

  const toast = useRef<Toast>(null);
  const navigate = useNavigate();
  const { backendAPIStore, authStore } = useContext(MobXProviderContext);
  const authExperience = useAuthExperience();
  const { completeStep, isStepActive } = useTour();
  const [loading, setLoading] = useState<boolean>(false);
  const [userGroups, setUserGroups] = useState<string[]>([]);
  const userRoleType = authStore.getUserRoleType();
  const isAdminUser = userGroups.includes(USER_GROUP_ADMIN);
  const mobile = useResponsive().isMobile;

  const [nurseProviders, setNurseProviders] = useState<any[]>([]);
  const [providersLoading, setProvidersLoading] = useState<boolean>(false);
  const [, setHasLoadedProvidersOnce] = useState<boolean>(false);
  const [savingProvider, setSavingProvider] = useState<boolean>(false);
  const [savingResident, setSavingResident] = useState<boolean>(false);
  const [providerDialogOpen, setProviderDialogOpen] = useState<boolean>(false);
  const [residentDialogOpen, setResidentDialogOpen] = useState<boolean>(false);
  const [providerDialogError, setProviderDialogError] = useState<string | null>(null);
  const [residentDialogError, setResidentDialogError] = useState<string | null>(null);
  const [residentProvider, setResidentProvider] = useState<any | null>(null);
  const [providerFormErrors, setProviderFormErrors] = useState<Record<string, string>>({});
  const [providerAddressSelected, setProviderAddressSelected] = useState<boolean>(false);
  const [providerAllowedResidents, setProviderAllowedResidents] = useState<number>(0);
  const [providerHomeResidentIds, setProviderHomeResidentIds] = useState<string[]>([]);

  const emptyProviderForm = {
    providerId: "",
    providerName: "",
    afhAddress: "",
    phone: "",
    email: "",
    linkedUserId: "",
  };
  const [providerForm, setProviderForm] = useState(emptyProviderForm);
  const [providerLookupLoading, setProviderLookupLoading] = useState<boolean>(false);
  const [providerLookupResult, setProviderLookupResult] = useState<any | null>(null);
  const [providerLookupAttempted, setProviderLookupAttempted] = useState<boolean>(false);
  const [accessUnlockActive, setAccessUnlockActive] = useState<boolean>(false);
  const [accessUnlockExpiresAt, setAccessUnlockExpiresAt] = useState<string | null>(null);
  const emptyResidentForm = {
    residentId: "",
    providerId: "",
    firstName: "",
    lastName: "",
    accessType: "manual",
  };
  const [residentForm, setResidentForm] = useState(emptyResidentForm);
  const [providerSearch, setProviderSearch] = useState("");
  const [selectedProviderId, setSelectedProviderId] = useState("");
  const [showArchivedProviders, setShowArchivedProviders] = useState(false);
  const [showArchivedResidents, setShowArchivedResidents] = useState(false);
  const [disconnectingSharedProviderId, setDisconnectingSharedProviderId] = useState("");
  const [showResidentArchiveLimitDialog, setShowResidentArchiveLimitDialog] = useState(false);
  const [residentArchiveLimitDetails, setResidentArchiveLimitDetails] = useState<{ billingPeriodStart?: string | null; billingPeriodEnd?: string | null }>({});
  const [showOnboardingWizard, setShowOnboardingWizard] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState<"provider" | "resident">("provider");
  const [, setDismissedOnboardingWizard] = useState(false);
  const [residentOnboardingActive, setResidentOnboardingActive] = useState(false);
  const resolvedWorkspaceDescription = workspaceDescription || authExperience.nurseWorkspace.workspaceDescription;
  const routeMode = "nurse" as "nurse" | "providerNurse";

  const sourceTagStyle = (tone: "manual" | "shared") => ({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "999px",
    padding: "0.15rem 0.55rem",
    fontSize: "0.74rem",
    fontWeight: 700,
    border: tone === "shared" ? "1px solid #bae6fd" : "1px solid #fde68a",
    background: tone === "shared" ? "#f0f9ff" : "#fffbeb",
    color: tone === "shared" ? "#0c4a6e" : "#92400e",
  });
  const actionTagStyle = (color: string, background: string, border: string, disabled = false) => ({
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.55 : 1,
    userSelect: "none" as const,
    background,
    color,
    border: `1px solid ${border}`,
  });
  const sharedResidentShiftStatusStyle = (resident: any) => {
    const accessActive = resident?.accessActive !== false;
    return {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "999px",
      padding: "0.15rem 0.55rem",
      fontSize: "0.74rem",
      fontWeight: 700,
      border: accessActive ? "1px solid #bbf7d0" : "1px solid #fde68a",
      background: accessActive ? "#f0fdf4" : "#fffbeb",
      color: accessActive ? "#166534" : "#92400e",
    } as const;
  };
  const getResidentDisplayName = (resident: any) =>
    `${resident?.firstName || ""} ${resident?.lastName || ""}`.trim() || resident?.residentName || "Resident";
  const isActiveProvider = (provider: any) => provider?.active !== false;
  const isActiveResident = (resident: any) => resident?.active !== false;
  const mergeProviderResidents = (residents: any[]) => {
    const residentMap = new Map<string, any>();
    (Array.isArray(residents) ? residents : []).forEach((resident: any) => {
      const residentKey = String(
        resident?.providerResidentId || resident?.residentId || resident?.displayResidentId || "",
      ).trim();
      if (!residentKey) {
        return;
      }
      const existingResident = residentMap.get(residentKey);
      if (!existingResident) {
        residentMap.set(residentKey, resident);
        return;
      }
      residentMap.set(residentKey, {
        ...existingResident,
        ...resident,
        residentId: String(existingResident?.residentId || resident?.residentId || residentKey).trim(),
        providerResidentId: String(existingResident?.providerResidentId || resident?.providerResidentId || residentKey).trim(),
        active: existingResident?.active !== false || resident?.active !== false,
        accessActive: existingResident?.accessActive !== false || resident?.accessActive !== false,
      });
    });
    return Array.from(residentMap.values());
  };
  const mergeProviders = (providers: any[]) => {
    const providerEntryMap = new Map<string, any>();
    (Array.isArray(providers) ? providers : []).forEach((provider: any) => {
      const providerKey = String(
        provider?.providerSource === "shared_connection"
          ? (provider?.homeId || provider?.sharedHomeId || provider?.providerId || provider?.sharedActorLinkId || "")
          : (
            provider?.sharedActorId
        || provider?.linkedUserId
        || provider?.email
        || provider?.sharedActorLinkId
        || provider?.providerId
        || ""
          )
      ).trim().toLowerCase();
      if (!providerKey) {
        return;
      }
      const existingProvider = providerEntryMap.get(providerKey);
      if (!existingProvider) {
        providerEntryMap.set(providerKey, {
          ...provider,
          residents: mergeProviderResidents(provider?.residents || []),
        });
        return;
      }
      providerEntryMap.set(providerKey, {
        ...existingProvider,
        ...provider,
        providerId: existingProvider?.providerId || provider?.providerId || providerKey,
        providerName: existingProvider?.providerName || provider?.providerName || "Shared Provider",
        providerHomeName: existingProvider?.providerHomeName || provider?.providerHomeName || "",
        email: existingProvider?.email || provider?.email || "",
        linkedUserId: existingProvider?.linkedUserId || provider?.linkedUserId || "",
        homeId: existingProvider?.homeId || provider?.homeId || "",
        sharedHomeId: existingProvider?.sharedHomeId || provider?.sharedHomeId || "",
        sharedActorId: existingProvider?.sharedActorId || provider?.sharedActorId || "",
        sharedActorLinkId: existingProvider?.sharedActorLinkId || provider?.sharedActorLinkId || "",
        sourceTags: Array.from(new Set([...(existingProvider?.sourceTags || []), ...(provider?.sourceTags || [])])),
        residents: mergeProviderResidents([...(existingProvider?.residents || []), ...(provider?.residents || [])]),
      });
    });
    return Array.from(providerEntryMap.values()).map((provider: any) => ({
      ...provider,
      residentCount: (provider?.residents || []).filter((resident: any) => resident?.active !== false).length,
    }));
  };
  const hasVisibleSharedShiftSummary = (resident: any) => {
    const shiftSummary = String(resident?.shiftSummary || "").trim();
    return Boolean(shiftSummary) && shiftSummary.toLowerCase() !== "no shift set";
  };
  const accessUnlockDaysLeft = useMemo(() => {
    if (!accessUnlockActive || !accessUnlockExpiresAt) {
      return 0;
    }
    const expiresAtMs = Date.parse(accessUnlockExpiresAt);
    if (!Number.isFinite(expiresAtMs)) {
      return 0;
    }
    const diffMs = expiresAtMs - Date.now();
    if (diffMs <= 0) {
      return 0;
    }
    return Math.max(1, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
  }, [accessUnlockActive, accessUnlockExpiresAt]);
  const formattedAccessUnlockEndDate = useMemo(() => {
    if (!accessUnlockExpiresAt) {
      return "";
    }
    return new Intl.DateTimeFormat("en-US", {
      timeZone: "America/Los_Angeles",
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(new Date(accessUnlockExpiresAt));
  }, [accessUnlockExpiresAt]);

  const resetProviderForm = () => {
    setProviderForm(emptyProviderForm);
    setProviderDialogError(null);
    setProviderFormErrors({});
    setProviderAddressSelected(false);
    setProviderLookupLoading(false);
    setProviderLookupResult(null);
    setProviderLookupAttempted(false);
  };

  const resetResidentForm = () => {
    setResidentForm(emptyResidentForm);
    setResidentProvider(null);
    setResidentDialogError(null);
  };

  const refreshNurseDashboard = async () => {
    setProvidersLoading(true);
    try {
      await backendAPIStore.init();
      const [providers, residentRoleLinks] = await Promise.all([
        backendAPIStore.getNurseProviders(),
        backendAPIStore.getResidentRoleLinks({ status: "approved", roleType: ROLE_TYPE_MHP }),
      ]);

      const manualProviders = await Promise.all(
        (Array.isArray(providers) ? providers : []).map(async (provider: any) => {
          const providerId = String(provider?.providerId || "").trim();
          if (!providerId || !isActiveProvider(provider)) return provider;

          try {
            const linkedResidents = await backendAPIStore.getNurseProviderResidents(providerId);
            return {
              ...provider,
              residents: Array.isArray(linkedResidents) ? linkedResidents : (provider?.residents || []),
            };
          } catch (_error) {
            return provider;
          }
        }),
      );
      const approvedSharedResidents = (residentRoleLinks || []).filter(
        (link: any) => String(link?.status || "").toLowerCase() === "approved",
      );
      const currentUserId = String(authStore.getUserId() || "").trim();
      const sharedHomeIds = Array.from(new Set(
        approvedSharedResidents
          .map((link: any) => String(link?.homeId || link?.metadata?.providerHomeId || "").trim())
          .filter(Boolean),
      ));
      const caregiverSharesByResidentKey = new Map<string, any>();
      if (currentUserId && sharedHomeIds.length) {
        const caregiverSharesByHome = await Promise.all(
          sharedHomeIds.map(async (homeId) => {
            try {
              const shares = await backendAPIStore.getCaregiverResidentSharesForHome(homeId, currentUserId);
              return { homeId, shares: Array.isArray(shares) ? shares : [] };
            } catch {
              return { homeId, shares: [] };
            }
          }),
        );
        caregiverSharesByHome.forEach(({ homeId, shares }) => {
          shares.forEach((share: any) => {
            const residentId = String(share?.residentId || "").trim();
            if (!residentId) {
              return;
            }
            caregiverSharesByResidentKey.set(`${homeId}:${residentId}`, share);
          });
        });
      }
      const providerMap = new Map<string, any>();

      manualProviders.forEach((provider: any) => {
        const providerKey = String(provider?.linkedUserId || provider?.email || provider?.providerId || "").toLowerCase();
        const manualResidents = (provider?.residents || []).map((resident: any) => ({
          ...resident,
          active: resident?.active !== false,
          accessType: "manual",
          canOpenProfile: true,
          canOpenAssessment: true,
          displayResidentId: resident?.residentId || "",
        }));

        providerMap.set(providerKey, {
          ...provider,
          active: provider?.active !== false,
          providerId: provider?.providerId || providerKey,
          residents: manualResidents,
          residentCount: manualResidents.filter((resident: any) => resident?.active !== false).length,
          isShared: false,
          isManual: true,
          sourceTags: ["Manual"],
          sharedActorLinkId: "",
          sharedActorId: "",
        });
      });

      approvedSharedResidents.forEach((residentLink: any) => {
        const providerActorId = String(
          residentLink?.metadata?.providerActorId || residentLink?.requesterActorId || "",
        ).trim();
        const providerHomeId = String(
          residentLink?.homeId || residentLink?.metadata?.providerHomeId || "",
        ).trim();
        const sharedProviderName = String(
          residentLink?.providerName
          || residentLink?.metadata?.providerName
          || "Shared Provider",
        ).trim() || "Shared Provider";
        const sharedHomeName = String(
          residentLink?.providerHomeName
          || residentLink?.homeName
          || residentLink?.metadata?.providerHomeName
          || "",
        ).trim();
        const sharedProviderEmail = String(
          residentLink?.providerEmail || residentLink?.metadata?.providerEmail || "",
        ).trim();
        const providerFallbackKey = String(
          providerHomeId || providerActorId || residentLink?.metadata?.providerEmail || residentLink?.actorLinkId,
        ).toLowerCase();
        let providerEntry = Array.from(providerMap.values()).find(
          (provider: any) => providerHomeId && String(provider?.homeId || provider?.sharedHomeId || "") === providerHomeId,
        );
        if (!providerEntry && !providerHomeId) {
          providerEntry = Array.from(providerMap.values()).find(
            (provider: any) => String(provider?.sharedActorLinkId || "") === String(residentLink?.actorLinkId || ""),
          );
        }
        if (!providerEntry && providerActorId && !providerHomeId) {
          providerEntry = Array.from(providerMap.values()).find(
            (provider: any) => String(provider?.sharedActorId || "") === providerActorId,
          );
        }
        if (!providerEntry && providerFallbackKey) {
          providerEntry = {
            providerId: `shared:${String(providerHomeId || residentLink?.actorLinkId || providerActorId || residentLink?.linkId || "").trim()}`,
            providerName: sharedProviderName,
            providerHomeName: sharedHomeName,
            afhAddress: "",
            phone: "",
            email: sharedProviderEmail,
            active: true,
            linkedUserId: providerActorId,
          linkedUserRoleType: ROLE_TYPE_PROVIDER,
            linkedDisplayName: sharedProviderName,
            providerSource: "shared_connection",
            isAfhHubUser: true,
            residents: [],
            residentCount: 0,
            isShared: true,
            isManual: false,
            sourceTags: ["Shared"],
            homeId: providerHomeId,
            sharedHomeId: providerHomeId,
            sharedActorLinkId: String(residentLink?.actorLinkId || "").trim(),
            sharedActorId: providerActorId,
          };
          providerMap.set(providerFallbackKey, providerEntry);
        }

        if (providerEntry) {
          providerEntry.providerName = sharedProviderName;
          providerEntry.providerHomeName = sharedHomeName || providerEntry.providerHomeName || "";
          providerEntry.linkedDisplayName = sharedProviderName;
          providerEntry.email = sharedProviderEmail || providerEntry.email || "";
          providerEntry.homeId = providerHomeId || providerEntry.homeId || "";
          providerEntry.sharedHomeId = providerHomeId || providerEntry.sharedHomeId || "";
        }

        if (!providerEntry) {
          return;
        }

        const sharedResidentId = String(residentLink?.roleResidentId || residentLink?.providerResidentId || "");
        const alreadyExists = (providerEntry.residents || []).some(
          (resident: any) =>
            String(resident?.residentId || "") === sharedResidentId
            || String(resident?.providerResidentId || "") === String(residentLink?.providerResidentId || ""),
        );

        if (alreadyExists) {
          return;
        }

        const resolvedHomeId = String(residentLink?.homeId || residentLink?.metadata?.providerHomeId || "").trim();
        const resolvedResidentId = String(residentLink?.providerResidentId || "").trim();
        const caregiverShare = caregiverSharesByResidentKey.get(`${resolvedHomeId}:${resolvedResidentId}`);
        providerEntry.residents = [
          ...(providerEntry.residents || []),
          {
            residentId: sharedResidentId || `shared:${residentLink?.linkId || residentLink?.providerResidentId || ""}`,
            binderResidentId: residentLink?.providerResidentId || sharedResidentId || "",
            assessmentResidentId: residentLink?.providerResidentId || sharedResidentId || "",
            carePlanResidentId: residentLink?.providerResidentId || sharedResidentId || "",
            delegationResidentId: residentLink?.providerResidentId || sharedResidentId || "",
            providerResidentId: residentLink?.providerResidentId || "",
            homeId: residentLink?.homeId || residentLink?.metadata?.providerHomeId || "",
            facilityType: String(residentLink?.facilityType || residentLink?.metadata?.facilityType || "").trim().toLowerCase(),
            binderAccessEnabled: residentLink?.binderAccessEnabled === true,
            assessmentAccessEnabled: normalizeSharedClinicalAccess(residentLink, "assessment"),
            carePlanAccessEnabled: normalizeSharedClinicalAccess(residentLink, "carePlan"),
            delegationAccessEnabled: normalizeSharedClinicalAccess(residentLink, "delegation"),
            behaviorLogAccessEnabled: true,
            incidentReportsAccessEnabled: true,
            dailyNotesAccessEnabled: true,
            emarAccessEnabled: true,
            adlAccessEnabled: true,
            shiftSummary: String(residentLink?.shiftSummary || "No shift set").trim() || "No shift set",
            accessActive: residentLink?.accessActive !== false,
            accessStatus: String(residentLink?.accessStatus || "").trim() || "Available Now",
            residentName: residentLink?.residentName || "Resident",
            firstName: String(residentLink?.firstName || "").trim(),
            lastName: String(residentLink?.lastName || "").trim(),
            active: residentLink?.active !== false,
            accessType: "shared",
            canOpenProfile: !!residentLink?.roleResidentId,
            canOpenAssessment: normalizeSharedClinicalAccess(residentLink, "assessment"),
            canOpenCarePlan: normalizeSharedClinicalAccess(residentLink, "carePlan"),
            canOpenDelegation: normalizeSharedClinicalAccess(residentLink, "delegation"),
            displayResidentId: residentLink?.providerResidentId || "",
          },
        ];
        providerEntry.residentCount = (providerEntry.residents || []).filter((resident: any) => resident?.active !== false).length;
      });

      const providerList = mergeProviders(Array.from(providerMap.values())).sort((first: any, second: any) =>
        String(first?.providerName || "").localeCompare(String(second?.providerName || "")),
      );
      const providerListWithSchedules = SHOW_RESIDENT_SCHEDULES ? await Promise.all(providerList.map(async (provider: any) => ({
        ...provider,
        residents: await Promise.all((provider?.residents || []).map(async (resident: any) => {
          const scheduleResidentId = String(resident?.providerResidentId || resident?.residentId || "").trim();
          if (!scheduleResidentId) return resident;
          try {
            const scheduleResponse = await backendAPIStore.getResidentSchedules(scheduleResidentId);
            return { ...resident, scheduleSummary: scheduleResponse?.summary || resident?.scheduleSummary };
          } catch {
            return resident;
          }
        })),
      }))) : providerList;

      setNurseProviders(providerListWithSchedules);
      setHasLoadedProvidersOnce(true);
      return providerListWithSchedules;
    } finally {
      setProvidersLoading(false);
    }
  };

  const confirmDisconnectSharedProvider = (provider: any) => {
    const providerId = String(provider?.providerId || "").trim();
    const providerHomeId = String(provider?.homeId || provider?.sharedHomeId || "").trim();
    const providerActorId = String(provider?.sharedActorId || provider?.linkedUserId || "").trim();
    const homeName = String(provider?.providerHomeName || "this shared home").trim();
    confirmDialog({
      header: "Disconnect Shared Provider",
      icon: "pi pi-exclamation-triangle",
      message: (
        <div>
          <p className="m-0">Disconnect from <strong>{homeName}</strong>?</p>
          <p>Shared resident access will be removed.</p>
          <p className="m-0">To reconnect, you will need to reach out to the provider and ask them to share access again.</p>
        </div>
      ),
      acceptLabel: "Disconnect",
      rejectLabel: "Cancel",
      acceptClassName: "p-button-danger",
      accept: async () => {
        setDisconnectingSharedProviderId(providerId);
        try {
          await backendAPIStore.disconnectSharedProvider({
            providerHomeId,
            providerActorId,
            roleType: ROLE_TYPE_MHP,
          });
          toast.current?.show({ severity: "success", summary: "Provider Disconnected", detail: `${homeName} was removed from your shared providers.`, life: 3000 });
          await refreshNurseDashboard();
        } catch (error: any) {
          toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to disconnect the shared provider.", life: 4000 });
        } finally {
          setDisconnectingSharedProviderId("");
        }
      },
    });
  };

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        await refreshNurseDashboard();
      } catch (error: any) {
        toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: error['message'], life: 3000 });
      }
    };
    fetchDashboardData();
  }, [backendAPIStore]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        await backendAPIStore.init();
        const authenticated = await authStore.isUserLoggedIn();
        if (authenticated) {
          setUserGroups(authStore.getUserGroups() || []);
          const userDetails = await backendAPIStore.getUserProfile(authStore.getUserId());
          if (!userDetails) {
            navigate(RouterAddress.PROFILE);
            return;
          }

          if (routeMode === "providerNurse" && userRoleType === ROLE_TYPE_PROVIDER) {
            const [subscriptionDetails, homes] = await Promise.all([
              backendAPIStore.getSubscription(authStore.getUserId(), ROLE_TYPE_PROVIDER),
              backendAPIStore.getHomeListForProvider(),
            ]);
            setAccessUnlockActive(!!subscriptionDetails?.accessUnlockActive);
            setAccessUnlockExpiresAt(String(subscriptionDetails?.accessUnlockExpiresAt || "").trim() || null);
            setProviderAllowedResidents(Number(subscriptionDetails?.allowedResidents || 0));
            const homeIds = (Array.isArray(homes?.homes) ? homes.homes : [])
              .map((home: any) => String(home?.homeId || "").trim())
              .filter(Boolean);
            const residentResults = await Promise.all(
              homeIds.map(async (homeId: string) => ({
                homeId,
                residents: await backendAPIStore.getResidentsForHome(homeId),
              })),
            );
            setProviderHomeResidentIds(
              residentResults
                .flatMap(({ residents }) => (Array.isArray(residents) ? residents : []))
                .filter((resident: any) => resident?.active !== false)
                .map((resident: any) => String(resident?.residentId || "").trim())
                .filter(Boolean),
            );
          } else {
            const subscriptionDetails = await backendAPIStore.getSubscription(authStore.getUserId(), ROLE_TYPE_NURSE);
            setAccessUnlockActive(!!subscriptionDetails?.accessUnlockActive);
            setAccessUnlockExpiresAt(String(subscriptionDetails?.accessUnlockExpiresAt || "").trim() || null);
            setProviderAllowedResidents(0);
            setProviderHomeResidentIds([]);
          }

        } else {
          navigate(RouterAddress.LOGIN_PAGE);
          return;
        }
      } catch (error: any) {
        toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: error['message'], life: 3000 });
        setAccessUnlockActive(false);
        setAccessUnlockExpiresAt(null);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [backendAPIStore, authStore, navigate, routeMode, userRoleType]);

  const submitProvider = async () => {
    const providerName = providerForm.providerName.trim();
    const afhAddress = providerForm.afhAddress.trim();
    const phone = providerForm.phone.trim();
    const email = providerForm.email.trim();
    const isExistingHubProvider = !!providerLookupResult?.canLinkAsProvider;

    const nextErrors: Record<string, string> = {};
    if (!isExistingHubProvider) {
      if (!providerName) {
        nextErrors.providerName = "Provider Name is required.";
      } else if (providerName.length > 100) {
        nextErrors.providerName = "Provider Name must be at most 100 characters long.";
      }

      if (!afhAddress) {
        nextErrors.afhAddress = `${authExperience.nurseWorkspace.providerAddressLabel} is required.`;
      } else if (!providerAddressSelected) {
        nextErrors.afhAddress = "Please select the full address from the Google suggestions.";
      }

      if (!phone) {
        nextErrors.phone = "Phone is required.";
      } else if (!/^[0-9()+\-\s]{7,20}$/.test(phone)) {
        nextErrors.phone = "Please enter a valid phone number.";
      }
    }

    if (!email) {
      nextErrors.email = "Email is required.";
    } else if (!/^\S+@\S+\.\S+$/.test(email)) {
      nextErrors.email = "Please enter a valid email address.";
    }

    setProviderFormErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      setProviderDialogError("Please fix the highlighted provider fields.");
      return;
    }

    setProviderDialogError(null);
    setSavingProvider(true);
    try {
      let lookupResult = providerLookupResult;
      if (email && /^\S+@\S+\.\S+$/.test(email)) {
        lookupResult = await backendAPIStore.lookupNurseProviderByEmail(email);
        setProviderLookupResult(lookupResult);
      }

      if (lookupResult?.alreadyAddedForNurse && lookupResult?.existingNurseProviderId !== providerForm.providerId) {
        setProviderDialogError("This provider email is already added to your nurse dashboard.");
        return;
      }

      if (lookupResult?.existsInAfhHub && !lookupResult?.canLinkAsProvider) {
        setProviderDialogError(authExperience.nurseWorkspace.existingBrandUserNotProviderMessage);
        return;
      }

      const shouldLinkExistingProvider = !!lookupResult?.canLinkAsProvider;

      const payload = {
        providerName: shouldLinkExistingProvider ? lookupResult?.displayName || providerName : providerName,
        afhAddress: shouldLinkExistingProvider ? "" : afhAddress,
        phone: shouldLinkExistingProvider ? "" : phone,
        email,
        linkedUserId: lookupResult?.canLinkAsProvider ? lookupResult.userId : providerForm.linkedUserId || "",
      };

      if (providerForm.providerId) {
        await backendAPIStore.updateNurseProvider({ providerId: providerForm.providerId, ...payload });
        toast.current?.show({ severity: "success", summary: "Updated", detail: "Provider updated successfully.", life: 2500 });
      } else {
        await backendAPIStore.addNurseProvider(payload);
        if (
          isStepActive("nurse-dashboard-add-provider-assessment")
          || isStepActive("nurse-dashboard-add-provider-delegation")
        ) {
          completeStep(
            isStepActive("nurse-dashboard-add-provider-assessment")
              ? "nurse-dashboard-add-provider-assessment"
              : "nurse-dashboard-add-provider-delegation",
          );
        }
        toast.current?.show({ severity: "success", summary: "Added", detail: "Provider added successfully.", life: 2500 });
      }

      const isWizardFlow = showOnboardingWizard && onboardingStep === "provider" && !providerForm.providerId;

      resetProviderForm();
      setProviderDialogOpen(false);
      const refreshedProviders = await refreshNurseDashboard();

      if (isWizardFlow) {
        const matchedProvider = (refreshedProviders || []).find((provider: any) =>
          String(provider?.email || "").trim().toLowerCase() === email.toLowerCase()
          || String(provider?.providerName || "").trim().toLowerCase() === String(payload.providerName || "").trim().toLowerCase()
        );

        if (matchedProvider?.providerId) {
          setSelectedProviderId(matchedProvider.providerId);
          setResidentProvider(matchedProvider);
          setResidentForm({
            residentId: "",
            providerId: matchedProvider.providerId || "",
            firstName: "",
            lastName: "",
            accessType: "manual",
          });
          setOnboardingStep("resident");
          setResidentOnboardingActive(true);
          setShowOnboardingWizard(false);
          window.setTimeout(() => openAddResidentDialog(matchedProvider), 0);
        }
      }
    } catch (error: any) {
      setProviderDialogError(error["message"] || "Unable to save provider.");
    } finally {
      setSavingProvider(false);
    }
  };

  const archiveProvider = async (providerId: string) => {
    try {
      await backendAPIStore.deleteNurseProvider(providerId);
      toast.current?.show({ severity: "success", summary: "Archived", detail: "Provider archived successfully.", life: 2500 });
      await refreshNurseDashboard();
    } catch (error: any) {
      if (String(error?.code || "").toUpperCase() === "RESIDENT_ARCHIVE_LIMIT_REACHED") {
        const details = error?.details?.details || error?.details || {};
        setResidentArchiveLimitDetails({
          billingPeriodStart: details?.billingPeriodStart,
          billingPeriodEnd: details?.billingPeriodEnd,
        });
        setShowResidentArchiveLimitDialog(true);
      } else {
        toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error["message"], life: 3000 });
      }
    }
  };

  const confirmArchiveProvider = (provider: any) => {
    confirmDialog({
      header: authExperience.nurseWorkspace.archiveProviderHeader,
      message: `Archive ${provider?.providerName || `this ${authExperience.nurseWorkspace.providerLabel.toLowerCase()}`} from your dashboard?`,
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      acceptLabel: "Archive",
      rejectLabel: "Cancel",
      accept: () => {
        void archiveProvider(provider.providerId);
      },
    });
  };

  const submitResident = async () => {
    setResidentDialogError(null);
    if (!residentForm.residentId && residentForm.accessType !== "shared" && !canAddResidentAcrossProviderWorkspaces) {
      setResidentDialogError(`Your active resident limit is ${Number(providerAllowedResidents || 0)}. Archive a resident or update Billing to add more active residents.`);
      return;
    }
    setSavingResident(true);
    try {
      const providerId = residentForm.providerId.trim();
      let createdResidentId = "";
      let successMessage = residentForm.residentId ? "Resident updated successfully." : "Resident added successfully.";
      if (residentForm.residentId) {
        await backendAPIStore.updateNurseProviderResident({
          residentId: residentForm.residentId,
          firstName: residentForm.firstName.trim(),
          lastName: residentForm.lastName.trim(),
        });
        createdResidentId = residentForm.residentId;
      } else {
        const createdResident = await backendAPIStore.addNurseProviderResident({
          providerId,
          firstName: residentForm.firstName.trim(),
          lastName: residentForm.lastName.trim(),
        });
        successMessage = String(createdResident?.message || successMessage).trim();
        if (
          isStepActive("nurse-dashboard-add-resident-assessment")
          || isStepActive("nurse-dashboard-add-resident-delegation")
        ) {
          completeStep(
            isStepActive("nurse-dashboard-add-resident-assessment")
              ? "nurse-dashboard-add-resident-assessment"
              : "nurse-dashboard-add-resident-delegation",
          );
        }
        createdResidentId = String(createdResident?.residentId || "").trim();
      }
      resetResidentForm();
      setResidentDialogOpen(false);
      await refreshNurseDashboard();
      setSelectedProviderId(providerId);
      toast.current?.show({
        severity: "success",
        summary: residentForm.residentId ? "Updated" : "Added",
        detail: successMessage,
        life: 3500,
      });
      if (residentOnboardingActive && onboardingStep === "resident") {
        setShowOnboardingWizard(false);
        setDismissedOnboardingWizard(true);
        setResidentOnboardingActive(false);
        if (createdResidentId) {
          const residentProfilePath = routeMode === "providerNurse"
            ? RouterAddress.getProviderNurseResidentProfilePagePath(createdResidentId)
            : RouterAddress.getNurseResidentProfilePagePath(createdResidentId);
          navigate(residentProfilePath);
          return;
        }
      }
    } catch (error: any) {
      setResidentDialogError(error["message"] || "Unable to save resident.");
    } finally {
      setSavingResident(false);
    }
  };

  const archiveResident = async (residentId: string) => {
    try {
      await backendAPIStore.deleteNurseProviderResident(residentId);
      await refreshNurseDashboard();
      toast.current?.show({ severity: "success", summary: "Archived", detail: "Resident archived successfully.", life: 2500 });
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error["message"], life: 3000 });
    }
  };

  const activateResident = async (provider: any, resident: any) => {
    try {
      await backendAPIStore.updateNurseProviderResident({
        residentId: String(resident?.residentId || "").trim(),
        firstName: String(resident?.firstName || "").trim(),
        lastName: String(resident?.lastName || "").trim(),
        active: true,
      });
      await refreshNurseDashboard();
      toast.current?.show({
        severity: "success",
        summary: "Resident Restored",
        detail: `${getResidentDisplayName(resident)} moved back to active residents.`,
        life: 2500,
      });
      setSelectedProviderId(String(provider?.providerId || ""));
      setShowArchivedResidents(false);
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error["message"] || "Unable to restore resident.", life: 3000 });
    }
  };

  const confirmArchiveResident = (resident: any) => {
    const residentName = getResidentDisplayName(resident);
    confirmDialog({
      header: "Archive Resident",
      message: `Archive ${residentName} from your dashboard?`,
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      acceptLabel: "Archive",
      rejectLabel: "Cancel",
      accept: () => {
        void archiveResident(resident.residentId);
      },
    });
  };

  const confirmActivateResident = (provider: any, resident: any) => {
    const residentName = getResidentDisplayName(resident);
    confirmDialog({
      header: "Activate Resident",
      message: `Restore ${residentName} to the active resident list?`,
      icon: "pi pi-info-circle",
      acceptClassName: "p-button-warning",
      acceptLabel: "Unarchive",
      rejectLabel: "Cancel",
      accept: () => {
        void activateResident(provider, resident);
      },
    });
  };

  const openAddProviderDialog = () => {
    resetProviderForm();
    setProviderDialogOpen(true);
  };

  const openEditProviderDialog = (provider: any) => {
    setProviderForm({
      providerId: provider.providerId || "",
      providerName: provider.providerName || "",
      afhAddress: provider.afhAddress || "",
      phone: provider.phone || "",
      email: provider.email || "",
      linkedUserId: provider.linkedUserId || "",
    });
    setProviderDialogError(null);
    setProviderFormErrors({});
    setProviderAddressSelected(!!provider.afhAddress);
    setProviderLookupResult(
      provider.linkedUserId ? {
        email: provider.email || "",
        existsInAfhHub: !!provider.isAfhHubUser,
        alreadyAddedForNurse: false,
        existingNurseProviderId: provider.providerId || "",
        userId: provider.linkedUserId || "",
        roleType: provider.linkedUserRoleType || "",
        canLinkAsProvider: provider.providerSource === "linked_existing_user",
        displayName: provider.linkedDisplayName || provider.providerName || "",
      } : null,
    );
    setProviderDialogOpen(true);
  };

  const lookupProviderEmail = async (email: string) => {
    const normalizedEmail = String(email || "").trim().toLowerCase();
    if (!normalizedEmail || !/^\S+@\S+\.\S+$/.test(normalizedEmail)) {
      setProviderLookupAttempted(false);
      setProviderLookupResult(null);
      return null;
    }

    setProviderLookupAttempted(true);
    setProviderLookupLoading(true);
    try {
      await backendAPIStore.init();
      const result = await backendAPIStore.lookupNurseProviderByEmail(normalizedEmail);
      setProviderLookupResult(result);

      if (result?.canLinkAsProvider) {
        setProviderForm((prev) => ({
          ...prev,
          email: normalizedEmail,
          linkedUserId: result.userId || "",
          providerName: prev.providerName || result.displayName || prev.providerName,
        }));
      } else {
        setProviderForm((prev) => ({
          ...prev,
          email: normalizedEmail,
          linkedUserId: "",
        }));
      }

      return result;
    } catch (error: any) {
      setProviderLookupResult(null);
      setProviderDialogError(error.message || "Unable to verify this provider email right now.");
      return null;
    } finally {
      setProviderLookupLoading(false);
    }
  };

  const openAddResidentDialog = (provider: any) => {
    setResidentProvider(provider);
    setResidentForm({
      residentId: "",
      providerId: provider.providerId || "",
      firstName: "",
      lastName: "",
      accessType: provider?.isShared || provider?.providerSource === "shared_connection" ? "shared" : "manual",
    });
    setResidentDialogError(null);
    setSelectedProviderId(provider.providerId || "");
    setResidentDialogOpen(true);
  };

  const openEditResidentDialog = (provider: any, resident: any) => {
    setResidentProvider(provider);
    setResidentForm({
      residentId: resident?.residentId || "",
      providerId: provider?.providerId || "",
      firstName: resident?.firstName || "",
      lastName: resident?.lastName || "",
      accessType: resident?.accessType || "manual",
    });
    setResidentDialogError(null);
    setSelectedProviderId(provider?.providerId || "");
    setResidentDialogOpen(true);
  };

  const activeNurseProviders = useMemo(
    () => nurseProviders.filter((provider: any) => isActiveProvider(provider)),
    [nurseProviders],
  );
  const archivedNurseProviders = useMemo(
    () => nurseProviders.filter((provider: any) => !isActiveProvider(provider)),
    [nurseProviders],
  );
  const filterProviders = useCallback((providers: any[]) => {
    const query = providerSearch.trim().toLowerCase();
    if (!query) return providers;

    return providers.filter((provider: any) => {
      const providerFields = [
        provider?.providerName,
        provider?.email,
        provider?.phone,
        provider?.afhAddress,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      if (providerFields.includes(query)) {
        return true;
      }

      const residents = provider?.residents || [];
      return residents.some((resident: any) =>
        [
          getResidentDisplayName(resident),
          resident?.residentId,
          resident?.providerResidentId,
          resident?.displayResidentId,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(query),
      );
    });
  }, [providerSearch]);
  const filteredActiveNurseProviders = useMemo(
    () => filterProviders(activeNurseProviders),
    [activeNurseProviders, filterProviders],
  );
  const filteredArchivedNurseProviders = useMemo(
    () => filterProviders(archivedNurseProviders),
    [archivedNurseProviders, filterProviders],
  );
  const visibleProviderOptions = useMemo(
    () => (showArchivedProviders
      ? [...filteredActiveNurseProviders, ...filteredArchivedNurseProviders]
      : filteredActiveNurseProviders),
    [filteredActiveNurseProviders, filteredArchivedNurseProviders, showArchivedProviders],
  );

  const visibleResidentCount = useMemo(
    () => visibleProviderOptions.reduce((sum: number, provider: any) => {
      const residents = Array.isArray(provider?.residents) ? provider.residents : [];
      const residentCount = residents.filter((resident: any) =>
        isActiveProvider(provider) ? isActiveResident(resident) : !isActiveResident(resident),
      ).length;
      return sum + residentCount;
    }, 0),
    [visibleProviderOptions],
  );
  const totalResidentCount = useMemo(
    () => nurseProviders.reduce((sum: number, provider: any) => {
      const residents = Array.isArray(provider?.residents) ? provider.residents : [];
      return sum + residents.filter((resident: any) => isActiveResident(resident)).length;
    }, 0),
    [nurseProviders],
  );
  const combinedProviderWorkspaceResidentCount = useMemo(() => {
    const uniqueResidentIds = new Set<string>(
      nurseProviders
        .flatMap((provider: any) => (Array.isArray(provider?.residents) ? provider.residents : []))
        .filter((resident: any) => isActiveResident(resident))
        .map((resident: any) => String(resident?.residentId || "").trim())
        .filter(Boolean),
    );
    (Array.isArray(providerHomeResidentIds) ? providerHomeResidentIds : [])
      .map((residentId) => String(residentId || "").trim())
      .filter(Boolean)
      .forEach((residentId) => uniqueResidentIds.add(residentId));
    return uniqueResidentIds.size;
  }, [nurseProviders, providerHomeResidentIds]);
  const canAddResidentAcrossProviderWorkspaces = true;

  useEffect(() => {
    if (!visibleProviderOptions.length) {
      setSelectedProviderId("");
      return;
    }

    const hasSelectedProvider = visibleProviderOptions.some(
      (provider: any) => String(provider?.providerId || "") === String(selectedProviderId || ""),
    );

    if (!hasSelectedProvider) {
      setSelectedProviderId(visibleProviderOptions[0].providerId);
    }
  }, [selectedProviderId, visibleProviderOptions]);

  const selectedProvider = useMemo(
    () => visibleProviderOptions.find((provider: any) => String(provider?.providerId || "") === String(selectedProviderId || "")) || null,
    [selectedProviderId, visibleProviderOptions],
  );
  const activeSelectedProviderResidents = useMemo(
    () => (selectedProvider?.residents || []).filter((resident: any) => isActiveResident(resident)),
    [selectedProvider],
  );
  const archivedSelectedProviderResidents = useMemo(
    () => (selectedProvider?.residents || []).filter((resident: any) => !isActiveResident(resident)),
    [selectedProvider],
  );
  const displayArchivedResidents = !!selectedProvider && !isActiveProvider(selectedProvider)
    ? true
    : showArchivedResidents;
  const displayedSelectedProviderResidents = useMemo(
    () => (displayArchivedResidents ? archivedSelectedProviderResidents : activeSelectedProviderResidents),
    [activeSelectedProviderResidents, archivedSelectedProviderResidents, displayArchivedResidents],
  );

  useEffect(() => {
    setShowArchivedResidents(false);
  }, [selectedProviderId]);

  const providerActionsBody = (provider: any) => (
    <div className="flex flex-wrap align-items-center gap-2">
      {provider?.isManual ? (
        <Button
          icon="pi pi-pencil"
          rounded
          outlined
          size="small"
          severity="warning"
          tooltip={`Edit ${authExperience.nurseWorkspace.providerLabel}`}
          tooltipOptions={{ position: "top" }}
          onClick={() => openEditProviderDialog(provider)}
          style={{
            width: "2rem",
            height: "2rem",
            minWidth: "2rem",
            padding: 0,
            boxShadow: "0 8px 20px rgba(249, 115, 22, 0.12)",
          }}
        />
      ) : null}
    </div>
  );

  const residentEditActionBody = (provider: any) => (resident: any) => (
    <>
      {isActiveResident(resident) && resident?.accessType !== "shared" ? (
        <Button
          label="Edit"
          severity="warning"
          rounded
          outlined
          size="small"
          onClick={() => openEditResidentDialog(provider, resident)}
        />
      ) : !isActiveResident(resident) && resident?.accessType !== "shared" ? (
        <Button
          label="Unarchive"
          severity="warning"
          rounded
          outlined
          size="small"
          onClick={() => confirmActivateResident(provider, resident)}
        />
      ) : null}
    </>
  );

  const residentActionsBody = (provider: any) => (resident: any) => (
    <div className="flex flex-wrap gap-2">
      {(() => {
        const shortcutStyle = (color: string, background: string, border: string) => ({
          borderRadius: "999px",
          padding: "0.28rem 0.72rem",
          background,
          color,
          border: `1px solid ${border}`,
          boxShadow: "none",
          fontSize: "0.8rem",
          fontWeight: 700,
        });
        const sharedHomeId = String(resident.homeId || "").trim();
        const sharedResidentId = resident.providerResidentId || resident.residentId;
        const residentFacilityType = String(resident?.facilityType || provider?.facilityType || "").trim().toLowerCase();
        const isEsfFacility = residentFacilityType === "esf";
        const sharedShiftLocked = resident?.accessType === "shared" && resident?.accessActive === false;
        const clinicalItems: MenuItem[] = [];
        const dailyActionItems: MenuItem[] = [];

        if (isActiveResident(resident)) {
          if (!isEsfFacility && (resident?.accessType !== "shared" ? resident?.canOpenAssessment !== false : resident?.assessmentAccessEnabled === true)) {
            clinicalItems.push({
              label: "Assessment",
              icon: "pi pi-file-edit",
              command: () => {
                if (isStepActive("nurse-dashboard-assessment")) {
                  completeStep("nurse-dashboard-assessment");
                }
                navigate(resident?.accessType === "shared"
                  ? RouterAddress.getSharedResidentAssessmentPagePath(sharedHomeId, resident.assessmentResidentId || resident.residentId)
                  : routeMode === "providerNurse"
                    ? RouterAddress.getProviderNurseResidentAssessmentPagePath(resident.assessmentResidentId || resident.residentId)
                    : RouterAddress.getNurseResidentAssessmentPagePath(resident.assessmentResidentId || resident.residentId));
              },
            });
          }
          if (resident?.accessType !== "shared" ? resident?.canOpenCarePlan !== false : resident?.carePlanAccessEnabled === true) {
            clinicalItems.push({
              label: "Care Plan",
              icon: "pi pi-directions-alt",
              command: () => {
                navigate(resident?.accessType === "shared"
                  ? RouterAddress.getSharedResidentCarePlanPagePathForFacility(sharedHomeId, resident.carePlanResidentId || resident.residentId, residentFacilityType)
                  : routeMode === "providerNurse"
                    ? RouterAddress.getProviderNurseResidentNcpPagePath(resident.carePlanResidentId || resident.residentId)
                    : RouterAddress.getNurseResidentNcpPagePath(resident.carePlanResidentId || resident.residentId));
              },
            });
          }
          if (resident?.accessType !== "shared" ? resident?.canOpenDelegation !== false : resident?.delegationAccessEnabled === true) {
            clinicalItems.push({
              label: "Delegation",
              icon: "pi pi-verified",
              command: () => {
                if (isStepActive("nurse-dashboard-delegation")) {
                  completeStep("nurse-dashboard-delegation");
                }
                navigate(resident?.accessType === "shared"
                  ? RouterAddress.getSharedResidentDelegationPagePath(sharedHomeId, resident.delegationResidentId || resident.residentId)
                  : routeMode === "providerNurse"
                    ? RouterAddress.getProviderNurseResidentDelegationPagePath(resident.residentId)
                    : RouterAddress.getNurseResidentDelegationPagePath(resident.residentId));
              },
            });
          }
          if (!isEsfFacility && (resident?.accessType !== "shared" || resident?.carePlanAccessEnabled === true)) {
            clinicalItems.push({
              label: "Foot & Nail Care",
              icon: "pi pi-heart",
              command: () => {
                navigate(
                  routeMode === "providerNurse"
                    ? RouterAddress.getProviderNurseResidentPodiatristPagePath(resident.residentId)
                    : RouterAddress.getNurseResidentPodiatristPagePath(resident.residentId)
                );
              },
            });
          }
        }

        if (SHOW_RESIDENT_SCHEDULES && resident?.accessType === "shared") {
          dailyActionItems.push({
            label: "Schedules",
            icon: "pi pi-calendar",
            command: () => navigate(RouterAddress.getResidentSchedulesPagePath(sharedHomeId, sharedResidentId)),
          });
        }
        if (resident?.accessType === "shared") {
          dailyActionItems.push({
            label: "Behavior",
            icon: "pi pi-clock",
            command: () => navigate(RouterAddress.getSharedResidentDailyBehaviorLogPagePath(sharedHomeId, sharedResidentId, "new")),
          });
        }
        if (resident?.accessType === "shared") {
          dailyActionItems.push({
            label: "Incident",
            icon: "pi pi-exclamation-circle",
            command: () => navigate(RouterAddress.getSharedResidentIncidentReportPagePath(sharedHomeId, sharedResidentId, "new")),
          });
        }
        if (resident?.accessType === "shared") {
          dailyActionItems.push({
            label: "Progress Notes",
            icon: "pi pi-pencil",
            command: () => navigate(RouterAddress.getSharedResidentDailyNotesPagePath(sharedHomeId, sharedResidentId, "new")),
          });
        }
        if (resident?.accessType === "shared") {
          dailyActionItems.push({
            label: "eMAR",
            icon: "pi pi-table",
            command: () => navigate(RouterAddress.getSharedResidentEmarPagePath(sharedHomeId, sharedResidentId)),
          });
        }
        if (resident?.accessType === "shared") {
          dailyActionItems.push({
            label: "ADL",
            icon: "pi pi-check-square",
            command: () => navigate(RouterAddress.getSharedResidentAdlPagePath(sharedHomeId, sharedResidentId)),
          });
        }
        if (resident?.accessType === "shared" && resident?.carePlanAccessEnabled !== false) {
          dailyActionItems.unshift({
            label: "Search Care Plan",
            icon: "pi pi-search",
            command: () => {
              if (sharedShiftLocked) {
                return;
              }
              navigate(RouterAddress.getSharedResidentCarePlanPagePath(sharedHomeId, sharedResidentId));
            },
          });
        }
        return (
          <>
            <Button
              type="button"
              label={resident?.accessType === "shared" ? "Profile" : "Dashboard"}
              text
              icon="pi pi-user"
              iconPos="left"
              size="small"
              disabled={sharedShiftLocked}
              style={shortcutStyle("#9a3412", "#fff7ed", "#fdba74")}
              onClick={() => navigate(
                resident?.accessType === "shared"
                  ? RouterAddress.getSharedResidentProfilePagePath(
                    String(resident.homeId || ""),
                    resident.providerResidentId || resident.residentId,
                  )
                  : routeMode === "providerNurse"
                    ? RouterAddress.getProviderNurseResidentProfilePagePath(resident.residentId)
                    : RouterAddress.getNurseResidentProfilePagePath(resident.residentId)
              )}
            />
            {resident?.accessType !== "shared" || resident?.binderAccessEnabled === true ? (
              <Button
                type="button"
                label="Binder"
                text
                icon="pi pi-book"
                iconPos="left"
                size="small"
                disabled={sharedShiftLocked}
                style={shortcutStyle("#0f766e", "#ecfeff", "#99f6e4")}
                onClick={() => navigate(
                  resident?.accessType === "shared"
                    ? RouterAddress.getSharedResidentBinderPagePath(
                      String(resident.homeId || ""),
                      resident.binderResidentId || resident.residentId,
                    )
                    : routeMode === "providerNurse"
                      ? RouterAddress.getProviderNurseResidentBinderPagePath(resident.residentId)
                      : RouterAddress.getNurseResidentBinderPagePath(resident.residentId)
                )}
              />
            ) : null}
            {isActiveResident(resident) && clinicalItems.length ? (
              <ActionMenuButton
                label="Clinical"
                size="small"
                rounded
                model={clinicalItems}
                disabled={sharedShiftLocked}
                style={shortcutStyle("#1d4ed8", "#eff6ff", "#bfdbfe")}
              />
            ) : null}
            {dailyActionItems.length ? (
              <>
              <ActionMenuButton
                label="Daily Tracker"
                size="small"
                rounded
                model={dailyActionItems}
                disabled={sharedShiftLocked}
                style={shortcutStyle("#0369a1", "#f0f9ff", "#bae6fd")}
              />
              {authExperience.featureFlags.providerTools.entryTracker && isActiveResident(resident) && resident?.accessType === "shared" ? (
                <ResidentEntryTrackerButton
                  residentId={String(sharedResidentId || "")}
                  residentName={`${resident?.firstName || ""} ${resident?.lastName || ""}`.trim() || "Resident"}
                  residentEntryStatus={resident?.residentEntryStatus}
                  disabled={sharedShiftLocked}
                  style={shortcutStyle("#0369a1", "#f0f9ff", "#bae6fd")}
                  createEntry={(targetResidentId, payload) => resident?.accessType === "shared"
                    ? backendAPIStore.createCaregiverResidentMovementEntry(targetResidentId, payload)
                    : backendAPIStore.createResidentMovementEntry(targetResidentId, payload)}
                  dashboardPath={`${RouterAddress.getSharedResidentEntryTrackerPagePath(sharedHomeId, String(sharedResidentId || ""))}?section=monthly`}
                />
              ) : null}
              </>
            ) : null}
          </>
        );
      })()}
    </div>
  );

  const residentsCountBody = (provider: any) => {
    const residents = Array.isArray(provider?.residents) ? provider.residents : [];
    const count = residents.filter((resident: any) =>
      isActiveProvider(provider) ? isActiveResident(resident) : !isActiveResident(resident),
    ).length;
    return (
      <span style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        minWidth: "1.6rem", height: "1.6rem", borderRadius: "999px",
        background: count > 0 ? "#eff6ff" : "#f1f5f9",
        border: count > 0 ? "1px solid #bfdbfe" : "1px solid #e2e8f0",
        color: count > 0 ? "#185DA2" : "#94a3b8",
        fontWeight: 700, fontSize: "0.82rem", padding: "0 0.4rem",
      }}>
        {count}
      </span>
    );
  };

  const providerIdentityBody = (provider: any, fontSize = "0.82rem") => {
    const isSharedProvider = provider?.isShared || provider?.providerSource === "shared_connection";
    const homeName = String(provider?.providerHomeName || "").trim();
    return (
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 800, color: "#0f172a", lineHeight: 1.35 }}>
          {isSharedProvider && homeName ? homeName : provider?.providerName}
        </div>
        {isSharedProvider && homeName ? (
          <div style={{ color: "#475569", fontSize, marginTop: "0.18rem", lineHeight: 1.35 }}>
            {provider?.providerName}
          </div>
        ) : null}
        {provider?.afhAddress ? (
          <div style={{ color: "#475569", fontSize, marginTop: "0.18rem", lineHeight: 1.35 }}>
            {provider.afhAddress}
          </div>
        ) : null}
        {provider?.phone ? (
          <div style={{ color: "#64748b", fontSize, marginTop: "0.14rem" }}>
            {provider.phone}
          </div>
        ) : null}
        {provider?.email ? (
          <div style={{ color: "#64748b", fontSize, marginTop: "0.14rem" }}>
            {provider.email}
          </div>
        ) : null}
      </div>
    );
  };

  const sharedProviderSettingsBody = (provider: any) => (
    <span onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
      <ActionMenuButton
        label=""
        ariaLabel="Shared provider settings"
        icon="pi pi-cog"
        size="small"
        rounded
        outlined
        severity="secondary"
        loading={disconnectingSharedProviderId === String(provider?.providerId || "")}
        style={{ width: "1.9rem", height: "1.9rem", padding: 0 }}
        menuStyle={{ minWidth: "12rem" }}
        model={[{
          label: "Disconnect Provider",
          icon: "pi pi-unlink",
          command: () => confirmDisconnectSharedProvider(provider),
        }]}
      />
    </span>
  );

  const renderResidentMobileTableRow = (provider: any, resident: any, index: number) => (
    <div
      key={resident.residentId || index}
      style={{
        display: "grid",
        gridTemplateColumns: "1fr",
        gap: "0.75rem",
        padding: "0.9rem 1rem",
        borderBottom: index === displayedSelectedProviderResidents.length - 1 ? "none" : "1px solid #eef2f7",
        background: isActiveResident(resident) ? "#ffffff" : "#f8fafc",
      }}
    >
      <div className="flex justify-content-between align-items-start gap-2 flex-wrap">
        <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 800, color: "#0f172a", lineHeight: 1.35 }}>
              {getResidentDisplayName(resident)}
            </div>
            {SHOW_RESIDENT_SCHEDULES ? <div className="flex align-items-center gap-2 flex-wrap" style={{ marginTop: "0.3rem" }}>
              <Tag value={`Schedules ${Number(resident?.scheduleSummary?.activeCount || 0)}`} severity="info" rounded />
              {Number(resident?.scheduleSummary?.missedCount || 0) > 0 ? <Tag value={`Missed ${Number(resident?.scheduleSummary?.missedCount || 0)}`} severity="danger" rounded /> : null}
            </div> : null}
          {resident?.accessType === "shared" ? (
            <div className="flex align-items-center gap-2 flex-wrap" style={{ marginTop: "0.45rem" }}>
              <span style={sourceTagStyle("shared")}>Shared</span>
              <span style={sharedResidentShiftStatusStyle(resident)}>
                {resident?.accessStatus || (resident?.accessActive === false ? "Outside Shift" : "Available Now")}
              </span>
              {hasVisibleSharedShiftSummary(resident) ? (
                <span style={{ color: "#64748b", fontSize: "0.88rem", fontWeight: 600 }}>
                  Shift: {resident?.shiftSummary}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
        <div className="flex align-items-center gap-2 flex-wrap">
          {residentEditActionBody(provider)(resident)}
        </div>
      </div>
      <div style={{ display: "grid", gap: "0.45rem" }}>
        <div style={{ color: "#64748b", fontWeight: 700, fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>
          Tools
        </div>
        <div className="flex flex-wrap gap-2">
          {residentActionsBody(provider)(resident)}
        </div>
      </div>
    </div>
  );

  const renderResidentDesktopTableRow = (provider: any, resident: any, index: number) => (
    <tr
      key={resident.residentId || index}
      style={{
        borderBottom: index === displayedSelectedProviderResidents.length - 1 ? "none" : "1px solid #eef2f7",
        background: isActiveResident(resident) ? "#ffffff" : "#f8fafc",
      }}
    >
      <td style={{ padding: "0.9rem 1rem", verticalAlign: "top" }}>
        <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 800, color: "#0f172a", lineHeight: 1.35 }}>
              {getResidentDisplayName(resident)}
            </div>
            {SHOW_RESIDENT_SCHEDULES ? <div className="flex align-items-center gap-2 flex-wrap" style={{ marginTop: "0.3rem" }}>
              <Tag value={`Schedules ${Number(resident?.scheduleSummary?.activeCount || 0)}`} severity="info" rounded />
              {Number(resident?.scheduleSummary?.missedCount || 0) > 0 ? <Tag value={`Missed ${Number(resident?.scheduleSummary?.missedCount || 0)}`} severity="danger" rounded /> : null}
            </div> : null}
          {resident?.accessType === "shared" ? (
            <div className="flex align-items-center gap-2 flex-wrap" style={{ marginTop: "0.45rem" }}>
              <span style={sourceTagStyle("shared")}>Shared</span>
            </div>
          ) : null}
        </div>
      </td>
      {provider?.isShared ? (
        <td style={{ padding: "0.9rem 1rem", verticalAlign: "top" }}>
          {resident?.accessType === "shared" ? (
            <div style={{ display: "grid", gap: "0.45rem" }}>
              <div>
                <span style={sharedResidentShiftStatusStyle(resident)}>
                  {resident?.accessStatus || (resident?.accessActive === false ? "Outside Shift" : "Available Now")}
                </span>
              </div>
              {hasVisibleSharedShiftSummary(resident) ? (
                <div style={{ color: "#475569", fontSize: "0.88rem", fontWeight: 600, lineHeight: 1.45 }}>
                  {resident?.shiftSummary}
                </div>
              ) : null}
            </div>
          ) : (
            <span style={{ color: "#94a3b8", fontSize: "0.88rem" }}>-</span>
          )}
        </td>
      ) : null}
      <td style={{ padding: "0.9rem 1rem", verticalAlign: "top" }}>
        <div className="flex flex-wrap gap-2">
          {residentActionsBody(provider)(resident)}
        </div>
      </td>
      <td style={{ padding: "0.9rem 1rem", verticalAlign: "top", textAlign: "right" }}>
        {residentEditActionBody(provider)(resident)}
      </td>
    </tr>
  );

  const providerDialogHeader = renderSurfaceHeader(
    providerForm.providerId ? `Edit ${authExperience.nurseWorkspace.providerLabel}` : `Add ${authExperience.nurseWorkspace.providerLabel}`,
    "",
    "pi pi-users",
  );

  const closeProviderDialog = () => {
    setProviderDialogOpen(false);
    resetProviderForm();
  };

  const providerDialogFooter = (
    <div className="flex flex-column gap-3" style={{ width: "100%" }}>
      {providerDialogError ? (
        <div style={{ color: "#dc2626", fontSize: "0.9rem", textAlign: "left" }}>{providerDialogError}</div>
      ) : null}
      <div className="flex justify-content-between align-items-center gap-2 flex-wrap" style={{ width: "100%" }}>
        <div>
          {providerForm.providerId ? (
            <Button
              type="button"
              label="Archive"
              severity="danger"
              rounded
              outlined
              onClick={() => {
                const provider = nurseProviders.find((item: any) => String(item?.providerId || "") === String(providerForm.providerId || ""));
                if (provider) {
                  closeProviderDialog();
                  confirmArchiveProvider(provider);
                }
              }}
            />
          ) : null}
        </div>
        <div className="flex align-items-center gap-2 flex-wrap">
          <Button
            type="button"
            label="Cancel"
            text
            onClick={closeProviderDialog}
          />
          <Button
            type="button"
            label={providerForm.providerId ? "Save" : (providerLookupResult?.canLinkAsProvider ? "Save" : `Save New ${authExperience.nurseWorkspace.providerLabel}`)}
            severity="warning"
            rounded
            loading={savingProvider}
            disabled={!providerForm.providerId && (
              providerLookupLoading
              || !providerLookupAttempted
              || !providerLookupResult
              || (providerLookupResult.existsInAfhHub && !providerLookupResult.canLinkAsProvider)
            )}
            onClick={() => { void submitProvider(); }}
          />
        </div>
      </div>
    </div>
  );
  const providerDialogContentStyle = {
    ...appDialogContentStyle,
    overflow: "visible",
  } as const;

  const handleProviderAddressChange = (event: {
    target: {
      value: string;
      place?: any;
    };
  }) => {
    const nextValue = event.target.value || "";
    const hasSelectedPlace = !!event.target.place;

    if (event.target.place) {
      extractLocationDetails(event.target.place);
    }

    setProviderForm((prev) => ({ ...prev, afhAddress: nextValue }));
    setProviderAddressSelected(hasSelectedPlace);
    setProviderFormErrors((prev) => {
      const nextErrors = { ...prev };
      delete nextErrors.afhAddress;
      return nextErrors;
    });
  };

  const residentDialogHeader = renderSurfaceHeader(
    residentForm.residentId ? "Edit Resident" : "Add Resident",
    "",
    residentForm.residentId ? "pi pi-user-edit" : "pi pi-user-plus",
  );

  const closeResidentDialog = () => {
    setResidentDialogOpen(false);
    resetResidentForm();
    setResidentOnboardingActive(false);
    if (userRoleType === ROLE_TYPE_MHP && totalResidentCount === 0) {
      setDismissedOnboardingWizard(true);
    }
  };

  const showResidentBillingButton = String(residentDialogError || "").toLowerCase().includes("resident slot")
    || String(residentDialogError || "").toLowerCase().includes("active resident limit")
    || String(residentDialogError || "").toLowerCase().includes("resident capacity");
  const showResidentArchiveButton = showResidentBillingButton && !residentForm.residentId;

  const residentDialogFooter = (
    <div className="flex flex-column gap-3" style={{ width: "100%" }}>
      {residentDialogError ? (
        <div style={{ color: "#dc2626", fontSize: "0.9rem", textAlign: "left" }}>{residentDialogError}</div>
      ) : null}
      <div className="flex justify-content-between align-items-center gap-2 flex-wrap" style={{ width: "100%" }}>
        <div>
          {residentForm.residentId && residentForm.accessType !== "shared" ? (
            <Button
              type="button"
              label="Archive"
              severity="danger"
              rounded
              outlined
              onClick={() => {
                const resident = selectedProvider?.residents?.find((item: any) => String(item?.residentId || "") === String(residentForm.residentId || ""));
                if (resident) {
                  closeResidentDialog();
                  confirmArchiveResident(resident);
                }
              }}
            />
          ) : null}
        </div>
        <div className="flex align-items-center gap-2 flex-wrap">
          {showResidentBillingButton ? (
            <Button
              type="button"
              label="Billing"
              severity="warning"
              rounded
              outlined
              onClick={() => navigate(RouterAddress.BILLING)}
            />
          ) : null}
          <Button
            type="button"
            label="Cancel"
            text
            onClick={closeResidentDialog}
          />
          <Button
            type="button"
            label={residentForm.residentId ? "Save" : "Save Resident"}
            severity="warning"
            rounded
            loading={savingResident}
            onClick={() => { void submitResident(); }}
          />
        </div>
      </div>
    </div>
  );

  const onboardingWizardHeader = renderSurfaceHeader(
    onboardingStep === "provider" ? "Let's get started" : "Add a resident",
    "",
    onboardingStep === "provider" ? "pi pi-users" : "pi pi-user-plus",
  );

  const onboardingWizardFooter = (
    <div className="flex justify-content-between align-items-center gap-2 flex-wrap" style={{ width: "100%" }}>
      <Button
        type="button"
        label="Close"
        text
        onClick={() => {
          setShowOnboardingWizard(false);
          setDismissedOnboardingWizard(true);
        }}
      />
      <Button
        type="button"
        label={
          onboardingStep === "provider"
            ? (providerDialogOpen ? "Provider Form Open" : "Open Provider Form")
            : (residentDialogOpen ? "Resident Form Open" : "Open Resident Form")
        }
        severity="warning"
        rounded
        onClick={() => {
          if (onboardingStep === "provider") {
            openAddProviderDialog();
            return;
          }

          if (residentProvider?.providerId) {
            openAddResidentDialog(residentProvider);
          }
        }}
        disabled={onboardingStep === "resident" && !residentProvider?.providerId}
      />
    </div>
  );

  const isExistingHubProvider = !!providerLookupResult?.canLinkAsProvider;
  const showManualProviderFields = providerForm.providerId
    ? !isExistingHubProvider
    : !!providerLookupResult && !providerLookupResult.existsInAfhHub;

  const isAuthorizedWorkspaceUser = isAdminUser || userRoleType === ROLE_TYPE_MHP;

  if (!loading && !userRoleType) {
    return <Unauthorized />;
  }

  return (
    <div style={{ padding: "1.25rem" }}>
      <Toast ref={toast} />
      <Dialog
        header="Archive limit reached"
        visible={showResidentArchiveLimitDialog}
        modal
        style={{ width: "34rem", maxWidth: "92vw" }}
        onHide={() => { setShowResidentArchiveLimitDialog(false); setResidentArchiveLimitDetails({}); }}
        footer={(
          <div className="flex justify-content-end gap-2 flex-wrap">
            <Button
              type="button"
              label="Close"
              severity="secondary"
              outlined
              rounded
              onClick={() => { setShowResidentArchiveLimitDialog(false); setResidentArchiveLimitDetails({}); }}
            />
            <Button
              type="button"
              label="View Subscription"
              icon="pi pi-credit-card"
              severity="warning"
              rounded
              onClick={() => {
                setShowResidentArchiveLimitDialog(false);
                setResidentArchiveLimitDetails({});
                navigate(RouterAddress.BILLING);
              }}
            />
          </div>
        )}
      >
        <div style={{ color: "#334155", lineHeight: 1.6 }}>
          You have reached the maximum number of resident archives allowed in this billing period.
          {residentArchiveLimitDetails.billingPeriodStart && residentArchiveLimitDetails.billingPeriodEnd ? (
            <div style={{ marginTop: "0.75rem", fontWeight: 600 }}>
              Billing period: {new Intl.DateTimeFormat("en-US", { timeZone: "America/Los_Angeles", month: "short", day: "numeric", year: "numeric" }).format(new Date(residentArchiveLimitDetails.billingPeriodStart))} – {new Intl.DateTimeFormat("en-US", { timeZone: "America/Los_Angeles", month: "short", day: "numeric", year: "numeric" }).format(new Date(residentArchiveLimitDetails.billingPeriodEnd))}
            </div>
          ) : null}
          <div style={{ marginTop: "0.75rem" }}>You can archive more residents when the next billing period begins or after updating your subscription.</div>
        </div>
      </Dialog>
      <div style={dashboardPageContainerStyle}>
        <Dialog
          visible={showOnboardingWizard}
          modal
          closable={false}
          closeOnEscape={false}
          dismissableMask={false}
          onHide={() => {
            setShowOnboardingWizard(false);
            setDismissedOnboardingWizard(true);
          }}
          header={onboardingWizardHeader}
          footer={onboardingWizardFooter}
          style={{ ...appDialogStyle, maxWidth: "34rem" }}
          headerStyle={appDialogHeaderStyle}
          contentStyle={appDialogContentStyle}
        >
          <div style={appAccentPanelStyle}>
            {onboardingStep === "provider" ? (
              <div className="flex flex-column gap-3">
                <div style={{ fontWeight: 700, color: "#9a3412" }}>{`Add your first ${authExperience.nurseWorkspace.providerLabel.toLowerCase()}.`}</div>
              </div>
            ) : (
              <div className="flex flex-column gap-3">
                <div style={{ fontWeight: 700, color: "#9a3412" }}>Add your first resident.</div>
                {residentProvider?.providerName ? (
                  <div style={appMutedTextStyle}>
                    {authExperience.nurseWorkspace.providerLabel}: <strong>{residentProvider.providerName}</strong>
                  </div>
                ) : null}
                <div style={appMutedTextStyle}>
                  Click <strong>Open Resident Form</strong>.
                </div>
                <div style={appMutedTextStyle}>
                  Enter the resident name.
                </div>
                <div style={appMutedTextStyle}>
                  Then save the resident.
                </div>
              </div>
            )}
          </div>
        </Dialog>
        {renderDashboardHeader(
          workspaceTitle,
          resolvedWorkspaceDescription,
          <div
            className="flex align-items-center gap-2 flex-wrap"
            style={{ width: "100%", justifyContent: mobile ? "flex-start" : "flex-end" }}
          >
            {accessUnlockActive && accessUnlockDaysLeft > 0 ? (
              <div
                style={{
                  borderRadius: "999px",
                  border: "1px solid #93c5fd",
                  background: "linear-gradient(135deg, #eff6ff 0%, #ffffff 52%, #dbeafe 100%)",
                  boxShadow: "0 10px 24px rgba(37, 99, 235, 0.1)",
                  padding: mobile ? "0.5rem 0.75rem" : "0.52rem 0.85rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.55rem",
                  flexWrap: "wrap",
                  textAlign: "center",
                  width: mobile ? "100%" : "auto",
                }}
              >
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "1.75rem",
                    height: "1.75rem",
                    borderRadius: "999px",
                    background: "#dbeafe",
                    color: "#1d4ed8",
                    boxShadow: "inset 0 0 0 1px #93c5fd",
                    fontSize: "0.78rem",
                  }}
                  className="pi pi-bolt"
                />
                <div
                  style={{
                    fontSize: mobile ? "0.82rem" : "0.86rem",
                    fontWeight: 700,
                    color: "#1e3a8a",
                    lineHeight: 1.35,
                  }}
                >
                  Access Unlock active:
                  <span style={{ color: "#1d4ed8", marginLeft: "0.3rem" }}>
                    {accessUnlockDaysLeft} day{accessUnlockDaysLeft === 1 ? "" : "s"} left
                    {formattedAccessUnlockEndDate ? ` until ${formattedAccessUnlockEndDate}` : ""}
                  </span>
                </div>
              </div>
            ) : null}
            <div style={{ ...dashboardSectionCardStyle, padding: "0.75rem 1rem", boxShadow: "none" }}>
              Providers: <strong className="ml-1">{activeNurseProviders.length}</strong>
              {providerSearch.trim() ? (
                <span className="ml-2" style={{ color: "#64748b", fontSize: "0.88rem" }}>
                  ({visibleProviderOptions.length} shown)
                </span>
              ) : null}
              <span className="ml-3">
                Residents: <strong className="ml-1">{visibleResidentCount}</strong>
              </span>
            </div>
          </div>,
        )}
        <div>
          {loading || providersLoading ? (
            <div className="card flex justify-content-center p-5">
              <ProgressSpinner role="status" style={{ color: "#185DA2" }} />
            </div>
          ) : (
            <div>
              {providerSearch.trim() && visibleProviderOptions.length === 0 ? (
                <div
                  style={{
                    ...dashboardSectionCardStyle,
                    padding: "2rem 1.5rem",
                    color: "#64748b",
                  }}
                >
                  <div
                    className="flex flex-column align-items-center justify-content-center text-center gap-3"
                    style={{ minHeight: "14rem" }}
                  >
                    <div style={{ maxWidth: "28rem", fontSize: "1rem", lineHeight: 1.7 }}>
                      {`No ${authExperience.nurseWorkspace.providerPluralLabel.toLowerCase()} or residents match this search.`}
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: mobile ? "1fr" : "minmax(300px, 360px) minmax(0, 1fr)",
                    gap: "1rem",
                    alignItems: "start",
                  }}
                >
                  <div
                    style={{
                      ...dashboardSectionCardStyle,
                      padding: "0.7rem",
                      boxShadow: "none",
                    }}
                  >
                    <div className="flex justify-content-between align-items-center gap-3 flex-wrap" style={{ margin: "0.2rem 0 0.75rem 0.25rem" }}>
                      <div style={{ fontWeight: 800, color: "#0f172a" }}>
                        {`Active ${authExperience.nurseWorkspace.providerPluralLabel} (${activeNurseProviders.length})`}
                      </div>
                      <div className="flex align-items-center gap-2 flex-wrap">
                        <Button
                          data-tour="nurse-dashboard-add-provider"
                          type="button"
                          label={`Add ${authExperience.nurseWorkspace.providerLabel}`}
                          severity="warning"
                          rounded
                          outlined
                          size="small"
                          onClick={openAddProviderDialog}
                        />
                      </div>
                    </div>
                    <div className="flex flex-column gap-2">
                      {filteredActiveNurseProviders.length === 0 ? (
                        <div style={{ color: "#64748b", fontStyle: "italic", padding: "0.35rem 0.5rem 0.55rem" }}>
                          {`No active ${authExperience.nurseWorkspace.providerPluralLabel.toLowerCase()} added yet.`}
                        </div>
                      ) : null}
                      {filteredActiveNurseProviders.map((provider: any) => {
                        const isSelected = String(provider?.providerId || "") === String(selectedProviderId || "");
                        return (
                          <div
                            key={provider.providerId}
                            onClick={() => setSelectedProviderId(provider.providerId)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                setSelectedProviderId(provider.providerId);
                              }
                            }}
                            role="button"
                            tabIndex={0}
                            aria-pressed={isSelected}
                            style={{
                              textAlign: "left",
                              width: "100%",
                              border: isSelected
                                ? "1px solid #fdba74"
                                : "1px solid #dbe7f3",
                              background: isSelected ? "#fff7ed" : "#ffffff",
                              borderRadius: "16px",
                              padding: "0.9rem 1rem",
                              cursor: "pointer",
                              boxShadow: isSelected ? "0 10px 24px rgba(249, 115, 22, 0.12)" : "none",
                            }}
                          >
                            <div className="flex justify-content-between align-items-start gap-2">
                              {providerIdentityBody(provider)}
                              <div>{residentsCountBody(provider)}</div>
                            </div>
                            <div className="flex align-items-center gap-2 flex-wrap" style={{ marginTop: "0.45rem" }}>
                              {!isActiveProvider(provider) ? (
                                <span style={{ ...sourceTagStyle("manual"), border: "1px solid #cbd5e1", background: "#f8fafc", color: "#475569" }}>
                                  Archived
                                </span>
                              ) : null}
                              {(provider.sourceTags || [])
                                .filter((tag: string) => tag === "Shared")
                                .map((tag: string) => (
                                  <span key={`${provider.providerId}-${tag}`} style={sourceTagStyle("shared")}>
                                    {tag}
                                  </span>
                                ))}
                              {provider?.isShared || provider?.providerSource === "shared_connection"
                                ? sharedProviderSettingsBody(provider)
                                : null}
                            </div>
                            {provider?.isManual ? (
                              <div className="flex justify-content-end" style={{ marginTop: "0.7rem" }}>
                                {providerActionsBody(provider)}
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                    {archivedNurseProviders.length ? (
                      <div style={{ marginTop: "1rem", paddingTop: "1rem", borderTop: "1px solid #e2e8f0" }}>
                        <div className="flex justify-content-between align-items-center gap-3 flex-wrap" style={{ margin: "0 0.75rem 0.75rem 0.25rem" }}>
                          <div style={{ fontWeight: 800, color: "#0f172a" }}>
                            Archived Providers
                          </div>
                          <Button
                            type="button"
                            label={
                              showArchivedProviders
                                ? `Showing Archived Providers (${archivedNurseProviders.length})`
                                : `Show Archived Providers (${archivedNurseProviders.length})`
                            }
                            icon={showArchivedProviders ? "pi pi-archive" : "pi pi-users"}
                            severity="warning"
                            rounded
                            outlined={!showArchivedProviders}
                            onClick={() => setShowArchivedProviders((current) => !current)}
                            style={{ minWidth: mobile ? "100%" : "18rem" }}
                          />
                        </div>
                        {showArchivedProviders ? (
                          filteredArchivedNurseProviders.length ? (
                            <div className="flex flex-column gap-2">
                              {filteredArchivedNurseProviders.map((provider: any) => {
                                const isSelected = String(provider?.providerId || "") === String(selectedProviderId || "");
                                const archivedResidentCount = (provider?.residents || []).filter((resident: any) => !isActiveResident(resident)).length;
                                return (
                                  <div
                                    key={provider.providerId}
                                    onClick={() => setSelectedProviderId(provider.providerId)}
                                    onKeyDown={(event) => {
                                      if (event.key === "Enter" || event.key === " ") {
                                        event.preventDefault();
                                        setSelectedProviderId(provider.providerId);
                                      }
                                    }}
                                    role="button"
                                    tabIndex={0}
                                    aria-pressed={isSelected}
                                    style={{
                                      textAlign: "left",
                                      width: "100%",
                                      border: isSelected ? "1px solid #fdba74" : "1px solid #cbd5e1",
                                      background: isSelected ? "#fff7ed" : "#f8fafc",
                                      borderRadius: "16px",
                                      padding: "0.9rem 1rem",
                                      cursor: "pointer",
                                      boxShadow: isSelected ? "0 10px 24px rgba(249, 115, 22, 0.12)" : "none",
                                      opacity: 0.88,
                                    }}
                                  >
                                    <div className="flex justify-content-between align-items-start gap-2">
                                      {providerIdentityBody(provider)}
                                      <div>{residentsCountBody(provider)}</div>
                                    </div>
                                    <div className="flex align-items-center gap-2 flex-wrap" style={{ marginTop: "0.45rem" }}>
                                      <span style={{ ...sourceTagStyle("manual"), border: "1px solid #cbd5e1", background: "#f8fafc", color: "#475569" }}>
                                        Archived
                                      </span>
                                      {(provider.sourceTags || [])
                                        .filter((tag: string) => tag === "Shared")
                                        .map((tag: string) => (
                                          <span key={`${provider.providerId}-${tag}`} style={sourceTagStyle("shared")}>
                                            {tag}
                                          </span>
                                        ))}
                                      {provider?.isShared || provider?.providerSource === "shared_connection"
                                        ? sharedProviderSettingsBody(provider)
                                        : null}
                                    </div>
                                    <div
                                      style={{
                                        color: archivedResidentCount ? "#475569" : "#94a3b8",
                                        fontSize: "0.8rem",
                                        marginTop: "0.42rem",
                                      }}
                                    >
                                      {archivedResidentCount
                                        ? `${archivedResidentCount} resident${archivedResidentCount === 1 ? "" : "s"} archived`
                                        : "No archived residents"}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div style={{ color: "#64748b", fontStyle: "italic", padding: "0.35rem 0.5rem 0.55rem" }}>
                              {`No archived ${authExperience.nurseWorkspace.providerPluralLabel.toLowerCase()} match this search.`}
                            </div>
                          )
                        ) : null}
                      </div>
                    ) : null}
                  </div>

                  <div
                    style={{
                      ...dashboardSectionCardStyle,
                      padding: "1rem",
                      boxShadow: "none",
                    }}
                  >
                    {selectedProvider ? (
                      <div>
                        <div className="flex justify-content-between align-items-center gap-3 flex-wrap" style={{ marginBottom: "0.75rem" }}>
                          <div>
                            <div style={{ fontWeight: 800, color: "#0f172a" }}>
                              {displayArchivedResidents
                                ? `Archived Residents (${archivedSelectedProviderResidents.length})`
                                : `Active Residents (${activeSelectedProviderResidents.length})`}
                            </div>
                          </div>
                          <div className="flex align-items-center gap-2 flex-wrap">
                            {selectedProvider?.homeId ? (
                              <Button
                                type="button"
                                label="Care Activity"
                                icon="pi pi-list-check"
                                severity="secondary"
                                outlined
                                rounded
                                size="small"
                                onClick={() => navigate((selectedProvider?.isShared || selectedProvider?.providerSource === "shared_connection")
                                  ? RouterAddress.getSharedHomeCareActivityPagePath(String(selectedProvider.homeId))
                                  : RouterAddress.getHomeCareActivityPagePath(String(selectedProvider.homeId)))}
                              />
                            ) : null}
                            {(selectedProvider?.isShared || selectedProvider?.providerSource === "shared_connection") && selectedProvider?.homeId ? (
                              <Button
                                type="button"
                                label="Facility Tasks"
                                icon="pi pi-check-square"
                                severity="warning"
                                outlined
                                rounded
                                size="small"
                                onClick={() => navigate(RouterAddress.getSharedHomeFacilityTasksPagePath(String(selectedProvider.homeId)))}
                              />
                            ) : null}
                            {(selectedProvider?.isManual || selectedProvider?.isShared || selectedProvider?.providerSource === "shared_connection") && !displayArchivedResidents && isActiveProvider(selectedProvider) ? (
                              <Button
                                data-tour="nurse-dashboard-add-resident"
                                icon="pi pi-user-plus"
                                label="Add Resident"
                                severity="warning"
                                rounded
                                outlined
                                size="small"
                                onClick={() => openAddResidentDialog(selectedProvider)}
                              />
                            ) : null}
                            {isActiveProvider(selectedProvider) && archivedSelectedProviderResidents.length ? (
                              <div className="flex align-items-center gap-2">
                                <label htmlFor="mhp-archived-residents-toggle" className="font-medium">
                                  {showArchivedResidents
                                    ? `Show Active (${activeSelectedProviderResidents.length})`
                                    : `Show Archived (${archivedSelectedProviderResidents.length})`}
                                </label>
                                <InputSwitch
                                  inputId="mhp-archived-residents-toggle"
                                  checked={showArchivedResidents}
                                  onChange={(event) => setShowArchivedResidents(!!event.value)}
                                  aria-label="Show archived residents"
                                />
                              </div>
                            ) : null}
                          </div>
                        </div>
                        <div>
                          {displayedSelectedProviderResidents.length ? (
                            mobile ? (
                              <div
                                style={{
                                  border: "1px solid #e2e8f0",
                                  borderRadius: "16px",
                                  overflow: "hidden",
                                  background: "#ffffff",
                                }}
                              >
                                <div
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr",
                                    gap: "0.75rem",
                                    padding: "0.8rem 1rem",
                                    background: "#f8fafc",
                                    borderBottom: "1px solid #e2e8f0",
                                    color: "#64748b",
                                    fontWeight: 700,
                                    fontSize: "0.78rem",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.05em",
                                  }}
                                >
                                  <div>Resident</div>
                                  <div>Tools</div>
                                </div>
                                {displayedSelectedProviderResidents.map((resident: any, index: number) => (
                                  renderResidentMobileTableRow(selectedProvider, resident, index)
                                ))}
                              </div>
                            ) : (
                              <div
                                style={{
                                  border: "1px solid #e2e8f0",
                                  borderRadius: "16px",
                                  overflow: "hidden",
                                  background: "#ffffff",
                                }}
                              >
                                <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                                  <thead>
                                    <tr
                                      style={{
                                        background: "#f8fafc",
                                        borderBottom: "1px solid #e2e8f0",
                                        color: "#64748b",
                                        fontWeight: 700,
                                        fontSize: "0.78rem",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.05em",
                                      }}
                                    >
                                      <th style={{ padding: "0.8rem 1rem", textAlign: "left", width: "20%" }}>Resident</th>
                                      {selectedProvider?.isShared ? (
                                        <th style={{ padding: "0.8rem 1rem", textAlign: "left", width: "26%" }}>Shift</th>
                                      ) : null}
                                      <th style={{ padding: "0.8rem 1rem", textAlign: "left", width: selectedProvider?.isShared ? "40%" : "66%" }}>Main Actions</th>
                                      <th style={{ padding: "0.8rem 1rem", textAlign: "right", width: "14%" }}>Tools</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {displayedSelectedProviderResidents.map((resident: any, index: number) => (
                                      renderResidentDesktopTableRow(selectedProvider, resident, index)
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )
                          ) : (
                            <div style={{ color: "#64748b", fontStyle: "italic" }}>
                              {displayArchivedResidents
                                ? "No archived residents."
                                : selectedProvider?.isShared && !selectedProvider?.isManual
                                  ? "No residents have been shared with you under this provider yet."
                                  : "No residents added yet. Use Add Resident to create the first one."}
                            </div>
                          )}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <Dialog
          visible={providerDialogOpen}
          modal
          header={providerDialogHeader}
          footer={providerDialogFooter}
          style={appDialogStyle}
          headerStyle={appDialogHeaderStyle}
          contentStyle={providerDialogContentStyle}
          onHide={() => {
            if (!providerDialogOpen) {
              return;
            }
            setProviderDialogOpen(false);
            if (onboardingStep === "provider" && activeNurseProviders.length === 0) {
              setShowOnboardingWizard(false);
              setDismissedOnboardingWizard(true);
            }
            resetProviderForm();
          }}
        >
          <div style={appAccentPanelStyle} className="mb-3">
            <div style={{ fontWeight: 700, color: "#9a3412" }}>{authExperience.nurseWorkspace.findProviderTitle}</div>
            <div className="mt-2" style={appMutedTextStyle}>
              {authExperience.nurseWorkspace.findProviderSubtitle}
            </div>
          </div>
          <div style={appFormSectionStyle}>
            <div className="field">
              <label htmlFor="providerEmail" className="mb-2 block" style={{ fontWeight: 600, color: "#334155" }}>
                Email Address
              </label>
              <div className="flex gap-2 align-items-start">
                <InputText
                  id="providerEmail"
                  value={providerForm.email}
                  onChange={(e) => {
                    setProviderForm((prev) => ({ ...prev, email: e.target.value, linkedUserId: "" }));
                    setProviderFormErrors((prev) => {
                      const nextErrors = { ...prev };
                      delete nextErrors.email;
                      return nextErrors;
                    });
                    setProviderLookupAttempted(false);
                    setProviderLookupResult(null);
                    setProviderDialogError(null);
                  }}
                  style={{ width: "100%" }}
                  placeholder="provider@email.com"
                />
                <Button
                  type="button"
                  label="Search"
                  severity="warning"
                  rounded
                  loading={providerLookupLoading}
                  disabled={!providerForm.email.trim() || !/^\S+@\S+\.\S+$/.test(providerForm.email.trim())}
                  onClick={() => { void lookupProviderEmail(providerForm.email); }}
                />
              </div>
              {providerFormErrors.email ? <small className="p-error">{providerFormErrors.email}</small> : null}
            </div>
            {providerLookupLoading ? (
              <div style={{ ...appMutedTextStyle, marginTop: "-0.25rem", marginBottom: "0.75rem" }}>
                {authExperience.nurseWorkspace.searchBrandLabel}
              </div>
            ) : null}
            {!providerForm.providerId && !providerLookupAttempted ? (
              <div style={{ ...appMutedTextStyle, marginTop: "-0.25rem", marginBottom: "0.75rem" }}>
                {authExperience.nurseWorkspace.enterProviderDetailsMessage}
              </div>
            ) : null}
            {providerLookupResult ? (
              <div
                style={{
                  marginBottom: "1rem",
                  padding: "0.9rem 1rem",
                  borderRadius: "14px",
                  border: providerLookupResult.canLinkAsProvider
                    ? "1px solid #86efac"
                    : providerLookupResult.existsInAfhHub
                      ? "1px solid #fca5a5"
                      : "1px solid #cbd5e1",
                  background: providerLookupResult.canLinkAsProvider
                    ? "#f0fdf4"
                    : providerLookupResult.existsInAfhHub
                      ? "#fef2f2"
                      : "#f8fafc",
                }}
              >
                <div
                  style={{
                    fontWeight: 700,
                    color: providerLookupResult.canLinkAsProvider
                      ? "#166534"
                      : providerLookupResult.existsInAfhHub
                        ? "#991b1b"
                        : "#0f172a",
                  }}
                >
                  {providerLookupResult.canLinkAsProvider
                    ? authExperience.nurseWorkspace.providerFoundMessage
                    : providerLookupResult.existsInAfhHub
                      ? authExperience.nurseWorkspace.notProviderMessage
                      : authExperience.nurseWorkspace.providerNotFoundMessage}
                </div>
                <div className="mt-2" style={{ ...appMutedTextStyle, lineHeight: 1.6 }}>
                  {providerLookupResult.canLinkAsProvider
                    ? authExperience.nurseWorkspace.saveProviderMessage
                    : providerLookupResult.existsInAfhHub
                      ? "Try a different email."
                      : authExperience.nurseWorkspace.enterProviderDetailsMessage}
                </div>
                {providerLookupResult.existsInAfhHub ? (
                  <div className="mt-3 grid">
                    <div className="col-12 md:col-6">
                      <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                        Name
                      </div>
                      <div style={{ color: "#0f172a", fontWeight: 600 }}>
                        {providerLookupResult.displayName
                          || `${providerLookupResult.firstName || ""} ${providerLookupResult.lastName || ""}`.trim()
                          || "Not available"}
                      </div>
                    </div>
                    <div className="col-12 md:col-6">
                      <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                        Role
                      </div>
                      <div style={{ color: "#0f172a", fontWeight: 600 }}>
                        {providerLookupResult.roleType === ROLE_TYPE_PROVIDER
                          ? authExperience.nurseWorkspace.providerLabel
                          : providerLookupResult.roleType || `${authExperience.brandName} User`}
                      </div>
                    </div>
                    <div className="col-12">
                      <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                        Email
                      </div>
                      <div style={{ color: "#0f172a", fontWeight: 600 }}>
                        {providerLookupResult.email || providerForm.email}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}
            {showManualProviderFields ? (
              <>
                <div className="field">
                  <span className="p-float-label">
                    <InputText
                      id="providerName"
                      value={providerForm.providerName}
                      onChange={(e) => {
                        setProviderForm((prev) => ({ ...prev, providerName: e.target.value }));
                        setProviderFormErrors((prev) => {
                          const nextErrors = { ...prev };
                          delete nextErrors.providerName;
                          return nextErrors;
                        });
                      }}
                      style={{ width: "100%" }}
                    />
                    <label htmlFor="providerName">{authExperience.nurseWorkspace.providerNameLabel}</label>
                  </span>
                  {providerFormErrors.providerName ? <small className="p-error">{providerFormErrors.providerName}</small> : null}
                </div>
                <div className="field">
                  <label className="mb-2 block" htmlFor="afhAddress">{authExperience.nurseWorkspace.providerAddressLabel}</label>
                  <SearchAddress
                    id="afhAddress"
                    name="afhAddress"
                    value={providerForm.afhAddress}
                    onChange={handleProviderAddressChange}
                    className={providerFormErrors.afhAddress ? "p-invalid" : ""}
                  />
                  {providerFormErrors.afhAddress ? <small className="p-error">{providerFormErrors.afhAddress}</small> : null}
                </div>
                <div className="field">
                  <span className="p-float-label">
                    <InputText
                      id="providerPhone"
                      value={providerForm.phone}
                      onChange={(e) => {
                        setProviderForm((prev) => ({ ...prev, phone: e.target.value }));
                        setProviderFormErrors((prev) => {
                          const nextErrors = { ...prev };
                          delete nextErrors.phone;
                          return nextErrors;
                        });
                      }}
                      style={{ width: "100%" }}
                    />
                    <label htmlFor="providerPhone">Phone</label>
                  </span>
                  {providerFormErrors.phone ? <small className="p-error">{providerFormErrors.phone}</small> : null}
                </div>
              </>
            ) : isExistingHubProvider ? (
              <div
                style={{
                  padding: "0.85rem 1rem",
                  borderRadius: "14px",
                  border: "1px dashed #86efac",
                  background: "#f8fffb",
                  color: "#166534",
                }}
              >
                {authExperience.nurseWorkspace.existingProviderMessage}
              </div>
            ) : null}
          </div>
        </Dialog>

        <Dialog
          visible={residentDialogOpen}
          modal
          header={residentDialogHeader}
          footer={residentDialogFooter}
          style={appDialogStyle}
          headerStyle={appDialogHeaderStyle}
          contentStyle={appDialogContentStyle}
          onHide={() => {
            if (!residentDialogOpen) {
              return;
            }
            closeResidentDialog();
          }}
        >
          <div style={appAccentPanelStyle} className="mb-3">
            <div style={{ fontWeight: 700, color: "#9a3412" }}>Add a resident</div>
            <div className="mt-2" style={appMutedTextStyle}>
              Enter the resident name and click Save.
            </div>
          </div>
          <div style={appFormSectionStyle}>
            <div className="field">
              <span className="p-float-label">
                <InputText
                  id="residentFirstName"
                  value={residentForm.firstName}
                  onChange={(e) => setResidentForm((prev) => ({ ...prev, firstName: e.target.value }))}
                  style={{ width: "100%" }}
                />
                <label htmlFor="residentFirstName">First Name</label>
              </span>
            </div>
            <div className="field mb-0">
              <span className="p-float-label">
                <InputText
                  id="residentLastName"
                  value={residentForm.lastName}
                  onChange={(e) => setResidentForm((prev) => ({ ...prev, lastName: e.target.value }))}
                  style={{ width: "100%" }}
                />
                <label htmlFor="residentLastName">Last Name</label>
              </span>
            </div>
          </div>
        </Dialog>
      </div>
    </div>
  );
};

const DashboardMhp = () => (
  <MhpDashboardWorkspace />
);

export default DashboardMhp;
